<template>
  <div class="metal-calculator-shortcode">
    <!-- Calculator Step -->
    <div v-if="showCalculator" class="o-metal-calculator">
      <p v-if="texts.calc_top_description" class="o-top-description">{{ texts.calc_top_description }}</p>

      <div class="o-controls">
        <div class="control left">
          <label for="gram-input">{{ isPCS ? texts.calc_weight_label_pcs : texts.calc_weight_label }}</label>
          <div class="value-box">
            <input type="number" id="gram-input" name="gram-input" class="gram-input" v-model.number="grams" min="1" max="10000" step="1" />
            <span class="gram-suffix">{{ isPCS ? 'PCS' : 'g' }}</span>
          </div>
          <input type="range" id="gram-slider" name="gram-slider" min="0" max="1000" v-model.number="sliderPosition" :style="{ '--range-progress': sliderPosition / 10 + '%' }" aria-label="Gram slider"/>
        </div>

        <div class="control right">
          <label for="purity-select">{{ texts.calc_purity_label }}</label>
          <select id="purity-select" name="purity-select" v-model="purity">
            <option v-for="option in purityOptions" :key="option.key" :value="option.key">{{ option.label }}</option>
          </select>
          <input 
            type="range"
            id="purity-slider"
            name="purity-slider"
            :min="0" 
            :max="Math.max(0, purityOptions.length - 1)" 
            v-model.number="purityIndex"
            :style="{ '--range-progress': (purityOptions.length > 1 ? (purityIndex / (purityOptions.length - 1)) * 100 : 0) + '%' }"
            aria-label="Purity slider"
          />
        </div>
      </div>

      <div class="o-result">
        <span class="result-label">{{ texts.calc_result_label }}</span>
        <div class="result-box">{{ formattedTotal }}</div>
        <span class="last-updated">Last updated {{ formattedLastUpdated }}</span>
      </div>

      <p v-if="texts.calc_description" class="o-description">{{ texts.calc_description }}</p>

      <div v-if="mode !== 'calculator'" class="o-actions">
        <button class="btn primary" @click="goToForm">{{ texts.calc_btn_sell }}</button>
        <a :href="texts.calc_btn_invest_url" class="btn primary">{{ texts.calc_btn_invest }}</a>
      </div>
    </div>

    <!-- Form Step -->
    <div v-if="showForm" class="o-form-step" :class="{ 'form-only': mode === 'form' }">
      <div class="form-container">
        <form @submit.prevent="submitForm" class="contact-form">
          <!-- Finland: the names come from Telia, so they are only shown (read-only) once verified -->
          <div v-if="!verificationRequired || isVerified" class="form-row two-col">
            <div class="form-field">
              <label for="firstName">{{ texts.form_label_firstname }}</label>
              <input type="text" id="firstName" name="firstName" v-model="form.firstName" :placeholder="texts.form_placeholder_firstname" :disabled="isVerified" required />
            </div>
            <div class="form-field">
              <label for="lastName">{{ texts.form_label_lastname }}</label>
              <input type="text" id="lastName" name="lastName" v-model="form.lastName" :placeholder="texts.form_placeholder_lastname" :disabled="isVerified" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="street">{{ texts.form_label_address }}</label>
              <input type="text" id="street" name="street" v-model="form.address" :placeholder="texts.form_placeholder_address" autocomplete="new-password" required />
            </div>
          </div>

          <div class="form-row two-col">
            <div class="form-field">
              <label for="zipCode">{{ texts.form_label_postcode }}</label>
              <input type="text" id="zipCode" name="zipCode" v-model="form.zipCode" :placeholder="texts.form_placeholder_postcode" required />
            </div>
            <div class="form-field">
              <label for="city">{{ texts.form_label_city }}</label>
              <input type="text" id="city" name="city" v-model="form.city" :placeholder="texts.form_placeholder_city" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="phone">{{ texts.form_label_phone }}</label>
              <div class="phone-input-group">
                <input type="text" id="countryCode" name="countryCode" class="phone-country-code" v-model="form.countryCode" required />
                <input type="tel" id="phone" name="phone" v-model="form.phone" :placeholder="texts.form_placeholder_phone" :minlength="texts.form_phone_min_length" :maxlength="texts.form_phone_max_length" :pattern="phonePattern" inputmode="numeric" required />
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="email">{{ texts.form_label_email }}</label>
              <input type="email" id="email" name="email" v-model="form.email" :placeholder="texts.form_placeholder_email" required />
            </div>
          </div>

          <!-- Business checkbox temporarily disabled
          <div class="form-row">
            <div class="form-field checkbox-field">
              <label class="checkbox-label">
                <input type="checkbox" id="isBusiness" name="isBusiness" v-model="form.isBusiness" />
                <span>{{ texts.form_label_business_checkbox }}</span>
              </label>
            </div>
          </div>
          -->

          <template v-if="form.isBusiness">
            <div class="form-row">
              <div class="form-field">
                <label for="businessName">{{ texts.form_label_business_name }}</label>
                <input type="text" id="businessName" name="businessName" v-model="form.businessName" placeholder="Bedriftsnavn" required />
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <label for="organizationNumber">{{ texts.form_label_organization_number }}</label>
                <input type="text" id="organizationNumber" name="organizationNumber" v-model="form.organizationNumber" placeholder="Organisasjonsnummer" required />
              </div>
            </div>
          </template>

          <div class="form-row two-col">
            <div class="form-field">
              <label for="goldGrams">{{ texts.form_label_gold }}</label>
              <input type="number" id="goldGrams" name="goldGrams" v-model="form.goldGrams" :placeholder="texts.form_placeholder_gold" min="0" step="1" />
            </div>
            <div class="form-field">
              <label for="silverGrams">{{ texts.form_label_silver }}</label>
              <input type="number" id="silverGrams" name="silverGrams" v-model="form.silverGrams" :placeholder="texts.form_placeholder_silver" min="0" step="1" />
            </div>
          </div>

          <div v-if="texts.form_quantity_enabled" class="form-row">
            <div class="form-field">
              <label for="quantity">{{ texts.form_label_return_packages }}</label>
              <select id="quantity" name="quantity" v-model="form.quantity" required>
                <option value="" disabled>-</option>
                <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>

          <div class="form-acceptance">
            <label class="acceptance-checkbox">
              <input type="checkbox" v-model="acceptedTAC" />
              <span>{{ texts.form_tac_label }} * <a :href="texts.form_tac_link_url" target="_blank" rel="noopener">{{ texts.form_tac_link_text }}</a></span>
            </label>
            <label class="acceptance-checkbox">
              <input type="checkbox" v-model="acceptedTOB" />
              <span>{{ texts.form_tob_label }} <a :href="texts.form_tob_link_url" target="_blank" rel="noopener">{{ texts.form_tob_link_url }}</a></span>
            </label>
          </div>

          <div class="form-actions">
            <!-- Finland: verify with Telia first, then the submit button takes its place -->
            <button v-if="verificationRequired && !isVerified" key="verify" type="button" class="btn btn-verify" :disabled="isVerifying" @click="startVerification">
              <span v-if="texts.form_btn_verify">{{ texts.form_btn_verify }}</span>
              <TeliaLogo class="telia-logo" />
            </button>
            <button v-else key="submit" type="submit" class="btn primary" :disabled="!acceptedTAC || !acceptedTOB">{{ texts.form_btn_submit }}</button>
          </div>

          <p class="privacy-notice">
            {{ texts.form_privacy_text }} <a :href="texts.form_privacy_link_url">{{ texts.form_privacy_link_text }}</a>
          </p>
        </form>

        <div v-if="mode !== 'form'" class="info-panel">
          <h3>{{ texts.info_title }}</h3>
          <div class="info-content" v-html="texts.info_content"></div>
        </div>
      </div>

      <button v-if="mode !== 'form'" class="btn-back" @click="step = 'calculator'">{{ texts.form_btn_back }}</button>
    </div>

    <!-- Captcha Modal -->
    <div v-if="showCaptchaModal" class="captcha-modal-overlay" @click.self="closeCaptchaModal">
      <div class="captcha-modal">
        <button class="captcha-close" @click="closeCaptchaModal">&times;</button>
        <h3>{{ texts.captcha_title || 'Verify you are human' }}</h3>
        <div id="recaptcha-container" ref="recaptchaContainer"></div>
        <p v-if="captchaError" class="captcha-error">{{ captchaError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import TeliaLogo from './TeliaLogo.vue';

export default {
  name: 'MetalCalculator',
  components: {
    TeliaLogo
  },
  props: {
    returposeConfig: {
      type: Object,
      default: null
    },
    initialTexts: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'full', // 'full', 'calculator', or 'form'
      validator: (value) => ['full', 'calculator', 'form'].includes(value)
    },
    instanceIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      step: 'calculator', // 'calculator' or 'form'
      grams: 15,
      purityIndex: 6, // Updated dynamically based on Metal calc settings.
      // Array of { key: countrySkuKey, label: displayName } objects.
      purityOptions: [],
      currency: 'NOK',
      // Prices loaded from WordPress via prop
      prices: {},
      units: {},
      labelsByKey: {},
      lastUpdated: null,
      apiUrl: null,
      refreshInterval: null,
      // Editable texts with defaults
      texts: {
        calc_top_description: '',
        calc_weight_label: 'Vekt i gram:',
        calc_weight_label_pcs: 'Antall',
        calc_purity_label: 'Metall / Renhet',
        calc_result_label: 'Anslått utbetaling',
        calc_description: '',
        calc_btn_sell: 'Selg gull og sølv',
        calc_btn_invest: 'Investér i edelmetaller',
        calc_btn_invest_url: 'https://nettbutikk.karasmussen.com',
        form_btn_submit: 'Bestill gratis pakke',
        form_btn_back: '← Tilbake til kalkulator',
        form_privacy_text: 'Les mer om hvordan vi bruker og behandler din personlige informasjon i vår',
        form_privacy_link_text: 'personvernpolicy',
        form_privacy_link_url: 'https://karasmussen.com/personvernerklaering/',
        form_label_firstname: 'Fornavn',
        form_label_lastname: 'Etternavn',
        form_label_address: 'Adresse',
        form_label_postcode: 'Postnummer',
        form_label_city: 'Poststed',
        form_label_phone: 'Mobilnummer',
        form_label_email: 'E-post',
        form_label_business_checkbox: 'Bedrift',
        form_label_business_name: 'Bedriftsnavn*',
        form_label_organization_number: 'Organisasjonsnummer*',
        form_label_gold: 'Gull (g)',
        form_label_silver: 'Sølv (g)',
        form_quantity_enabled: false,
        form_label_return_packages: 'Antall returposer',
        form_placeholder_firstname: 'Fornavn',
        form_placeholder_lastname: 'Etternavn',
        form_placeholder_address: 'Adresse',
        form_placeholder_postcode: 'Postnummer',
        form_placeholder_city: 'Poststed',
        form_placeholder_phone: 'Mobilnummer',
        form_phone_country_code: '+47',
        form_phone_min_length: 8,
        form_phone_max_length: 8,
        form_placeholder_email: 'E-post',
        form_placeholder_gold: 'Hvor mye gull i g',
        form_placeholder_silver: 'Hvor mye sølv i g',
        form_success_message: 'Takk for din henvendelse! Vi kontakter deg snart.',
        form_error_message: 'Noe gikk galt. Vennligst prøv igjen.',
        form_btn_verify: 'Verify with',
        form_verify_error_message: 'Verification failed. Please try again.',
        form_tac_label: 'Jeg godtar disse betingelsene',
        form_tac_link_text: 'personvernerklaering',
        form_tac_link_url: 'https://karasmussen.com/personvernerklaering/',
        form_tob_label: 'Jeg har lest og forstått forretningsvilkårene.',
        form_tob_link_url: 'https://karasmussen.com/forretningsvilkar/',
        info_title: 'Hvorfor selge til oss?',
        info_content: '<strong>Markedets beste pris</strong> – som industriaktør betaler vi mer enn gullsmeder og oppkjøpere<br><br><strong>Trygt oppgjør</strong> – rask betaling direkte til konto<br><br><strong>Ryddig prosess</strong> – profesjonell vurdering og full transparens<br><br><strong>Bærekraftig valg</strong> – dine metaller går tilbake i produksjon og gjenvinning'
      },
      form: {
        firstName: '',
        lastName: '',
        address: '',
        zipCode: '',
        city: '',
        countryCode: '+47',
        phone: '',
        email: '',
        goldGrams: '',
        silverGrams: '',
        quantity: '',
        isBusiness: false,
        businessName: '',
        organizationNumber: ''
      },
      // Acceptance checkboxes
      acceptedTAC: false,
      acceptedTOB: false,
      // Captcha state
      showCaptchaModal: false,
      captchaResponse: null,
      captchaError: null,
      recaptchaWidgetId: null,
      recaptchaSiteKey: '',
      googleMapsApiKey: '',
      googleMapsCountry: '',
      submitUrl: null,
      // Telia verification
      verificationApiUrl: '',
      verificationRedirectUri: '',
      isVerified: false,
      verificationId: null,
      isVerifying: false,
      storeCode: '',
      isSubmitting: false,
      submissionGuid: null,
      lastSubmittedFormHash: null
    };
  },
  created() {
    // Load prices from prop passed by main.js (from data attribute)
    if (this.returposeConfig) {
      if (this.returposeConfig.prices) {
        this.prices = this.returposeConfig.prices;
      }
      if (this.returposeConfig.units) {
        this.units = this.returposeConfig.units;
      }
      if (this.returposeConfig.labelsByKey) {
        this.labelsByKey = this.returposeConfig.labelsByKey;
      }
      // Build purityOptions as { key, label } pairs.
      this.purityOptions = this.buildPurityOptions(this.prices, this.labelsByKey);
      if (this.returposeConfig.currency) {
        this.currency = this.returposeConfig.currency;
      }
      if (this.returposeConfig.lastUpdated) {
        this.lastUpdated = this.returposeConfig.lastUpdated;
      }
      if (this.returposeConfig.apiUrl) {
        this.apiUrl = this.returposeConfig.apiUrl;
      }
      if (this.returposeConfig.submitUrl) {
        this.submitUrl = this.returposeConfig.submitUrl;
      }
      if (this.returposeConfig.recaptchaSiteKey) {
        this.recaptchaSiteKey = this.returposeConfig.recaptchaSiteKey;
      }
      if (this.returposeConfig.googleMapsApiKey) {
        this.googleMapsApiKey = this.returposeConfig.googleMapsApiKey;
      }
      if (this.returposeConfig.googleMapsCountry) {
        this.googleMapsCountry = this.returposeConfig.googleMapsCountry;
      }
      if (this.returposeConfig.storeCode) {
        this.storeCode = this.returposeConfig.storeCode;
      }
      if (this.returposeConfig.verificationApiUrl) {
        this.verificationApiUrl = this.returposeConfig.verificationApiUrl;
      }
      if (this.returposeConfig.verificationRedirectUri) {
        this.verificationRedirectUri = this.returposeConfig.verificationRedirectUri;
      }
      // Load default slider values from settings
      if (this.returposeConfig.defaultGrams !== undefined) {
        this.grams = this.returposeConfig.defaultGrams;
      }
      if (this.returposeConfig.defaultPurityIndex !== undefined) {
        // Ensure purityIndex is within bounds
        const maxIndex = this.purityOptions.length - 1;
        this.purityIndex = Math.min(this.returposeConfig.defaultPurityIndex, maxIndex);
      }
    }
    
    // Load texts from prop passed by main.js (from data attribute)
    if (this.initialTexts) {
      this.texts = { ...this.texts, ...this.initialTexts };
    }

    // Country code is prefilled from settings, but the user can edit it
    this.form.countryCode = this.texts.form_phone_country_code;

    if (this.verificationRequired && this.mode !== 'calculator') {
      this.completeVerification();
    }
    
    // Refresh prices every 30 minutes
    if (this.apiUrl) {
      this.refreshInterval = setInterval(() => {
        this.fetchPrices();
      }, 30 * 60 * 1000);
    }
  },
  mounted() {
    if (this.showForm) {
      this.initAddressAutocomplete();
    }
  },
  watch: {
    showForm(visible) {
      if (visible) {
        this.initAddressAutocomplete();
      }
    }
  },
  beforeDestroy() {
    // Clean up interval when component is destroyed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  computed: {
    // Display logic based on mode
    showCalculator() {
      if (this.mode === 'calculator') return true;
      if (this.mode === 'form') return false;
      // 'full' mode: show calculator when step is 'calculator'
      return this.step === 'calculator';
    },
    showForm() {
      if (this.mode === 'form') return true;
      if (this.mode === 'calculator') return false;
      // 'full' mode: show form when step is 'form'
      return this.step === 'form';
    },
    sliderPosition: {
      get() {
        // Convert grams to slider position, logarithmic scale
        const grams = Math.max(1, Math.min(10000, this.grams));
        const normalizedPos = Math.pow(Math.log10(grams) / 4, 1.765);
        return Math.round(normalizedPos * 1000);
      },
      set(val) {
        // Convert slider position to grams, logarithmic scale
        const pos = val / 1000; // normalize to 0-1
        const exponent = Math.pow(pos, 0.567);
        this.grams = Math.max(1, Math.round(Math.pow(10000, exponent)));
      }
    },
    purity: {
      get() {
        const current = this.purityOptions[this.purityIndex];
        if (current) return current.key;
        const first = this.purityOptions[0];
        return first ? first.key : '';
      },
      set(val) {
        const index = this.purityOptions.findIndex(o => o.key === val);
        if (index !== -1) {
          this.purityIndex = index;
        }
      }
    },
    isPCS() {
      return this.units[this.purity] === 'PCS';
    },
    verificationRequired() {
      return !!this.verificationApiUrl;
    },
    phonePattern() {
      return `[0-9]{${this.texts.form_phone_min_length},${this.texts.form_phone_max_length}}`;
    },
    gramsDisplay() {
      return Math.round(this.grams);
    },
    pricePerGram() {
      return parseFloat(this.prices[this.purity]) || 0;
    },
    total() {
      return this.gramsDisplay * this.pricePerGram;
    },
    formattedTotal() {
      return `${this.total.toLocaleString('nb-NO')} ${this.currency}`;
    },
    formattedLastUpdated() {
      if (!this.lastUpdated) return '';
      const date = new Date(this.lastUpdated);
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      return `${day}/${month} kl ${hours}:${minutes} GMT`;
    }
  },
  methods: {
    buildPurityOptions(prices, labelsByKey) {
      const keys = Object.keys(prices || {});
      return keys.map(k => ({
        key: k,
        label: (labelsByKey && labelsByKey[k]) ? labelsByKey[k] : k
      }));
    },
    goToForm() {
      this.step = 'form';
    },
    initAddressAutocomplete() {
      if (!this.googleMapsApiKey || this.autocomplete) return;

      this.loadGoogleMaps(() => {
        this.$nextTick(() => {
          const input = document.getElementById('street');
          if (!input) return;

          const options = {
            fields: ['address_components'],
            types: ['address']
          };
          // If country is configured, limit search to that country. If not configured, search all addresses.
          if (this.googleMapsCountry) {
            options.componentRestrictions = { country: [this.googleMapsCountry] };
          }

          this.autocomplete = new window.google.maps.places.Autocomplete(input, options);
          this.autocomplete.addListener('place_changed', this.fillInAddress);

          // Google appends the dropdown to <body> and positions it absolutely,
          // which drifts inside transformed containers. Anchor it to the field.
          this.$nextTick(() => {
            const containers = document.querySelectorAll('.pac-container');
            const container = containers[containers.length - 1];
            if (container) {
              input.parentNode.appendChild(container);
            }

            // The widget forces autocomplete="off" on attach, which Chrome ignores for address fields.
            // Override with "new-password" (to avoid saved addresses overlapping the search suggestions). No autocomplete for new passwords.
            input.setAttribute('autocomplete', 'new-password');
          });
        });
      });
    },
    loadGoogleMaps(callback) {
      if (window.google && window.google.maps && window.google.maps.places) {
        callback();
        return;
      }

      const existing = document.getElementById('google-maps-script');
      if (existing) {
        existing.addEventListener('load', callback);
        return;
      }

      window.initGoogleMapsAutocomplete = callback;

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&callback=initGoogleMapsAutocomplete&libraries=places&v=weekly`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    },
    fillInAddress() {
      const place = this.autocomplete.getPlace();
      if (!place || !place.address_components) return;

      let route = '';
      let streetNumber = '';
      let postalTown = '';
      let locality = '';

      for (const component of place.address_components) {
        switch (component.types[0]) {
          case 'street_number':
            streetNumber = component.long_name;
            break;
          case 'route':
            route = component.long_name;
            break;
          case 'postal_code':
            this.form.zipCode = component.long_name;
            break;
          case 'postal_town':
            postalTown = component.long_name;
            break;
          case 'locality':
            locality = component.long_name;
            break;
        }
      }

      this.form.address = `${route} ${streetNumber}`.trim();
      this.form.city = postalTown || locality;
    },
    async fetchPrices() {
      if (!this.apiUrl) return;
      try {
        const response = await fetch(this.apiUrl);
        const data = await response.json();
        if (data.prices) {
          this.prices = data.prices;
        }
        if (data.labelsByKey) {
          this.labelsByKey = data.labelsByKey;
        }
        if (data.units) {
          this.units = data.units;
        }

        this.purityOptions = this.buildPurityOptions(this.prices, this.labelsByKey);

        if (data.currency) {
          this.currency = data.currency;
        }
        if (data.lastUpdated) {
          this.lastUpdated = data.lastUpdated;
        }
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      }
    },
    async startVerification() {
      this.isVerifying = true;
      try {
        // Same request as the webshop
        const response = await fetch(`${this.verificationApiUrl}/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // flow-2 = verification without a Magento customer
            'X-Verification-Flow': 'flow-2'
          },
          body: JSON.stringify({
            state: { path: window.location.pathname },
            // Exact URL Telia sends the user back to; must be the same on /init and /checkout
            redirectUri: this.verificationRedirectUri || undefined
          })
        });
        const data = await response.json();
        if (!response.ok || !data.result || !data.result.redirectUrl) {
          throw new Error('Missing Telia redirect URL');
        }

        sessionStorage.setItem('metalCalculatorVerification', JSON.stringify({
          savedAt: Date.now(),
          instanceIndex: this.instanceIndex,
          nonce: data.result.nonce,
          step: this.step,
          grams: this.grams,
          purityIndex: this.purityIndex,
          form: this.form,
          acceptedTAC: this.acceptedTAC,
          acceptedTOB: this.acceptedTOB
        }));
        document.cookie = `metal_calculator_verification_return=${encodeURIComponent(window.location.href)}; path=/; max-age=900; SameSite=Lax`;
        window.location.href = data.result.redirectUrl;
      } catch (error) {
        this.isVerifying = false;
        console.error('Verification init error:', error);
        alert(this.texts.form_verify_error_message);
      }
    },
    async completeVerification() {
      const saved = JSON.parse(sessionStorage.getItem('metalCalculatorVerification'));
      if (!saved) return;

      // Delete form state left behind when the user never came back from Telia
      if (Date.now() - saved.savedAt > 15 * 60 * 1000) {
        sessionStorage.removeItem('metalCalculatorVerification');
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      if (!code || !state) return;

      // Only the form that started the verification handles the return
      if (saved.instanceIndex !== this.instanceIndex) return;

      sessionStorage.removeItem('metalCalculatorVerification');
      params.delete('code');
      params.delete('state');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);

      // Fill the form with saved values
      this.step = saved.step;
      this.grams = saved.grams;
      this.purityIndex = saved.purityIndex;
      this.form = { ...this.form, ...saved.form };
      this.acceptedTAC = saved.acceptedTAC;
      this.acceptedTOB = saved.acceptedTOB;

      // The page loads at the top after the redirect, so scroll to this form
      const scrollToForm = () => this.$el.scrollIntoView({ block: 'start' });
      this.$nextTick(scrollToForm);
      if (document.readyState !== 'complete') {
        window.addEventListener('load', scrollToForm, { once: true });
      }

      this.isVerifying = true;
      try {
        const response = await fetch(`${this.verificationApiUrl}/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Verification-Flow': 'flow-2'
          },
          body: JSON.stringify({
            code,
            state,
            nonce: saved.nonce,
            redirectUri: this.verificationRedirectUri || undefined
          })
        });
        const data = await response.json();
        if (!response.ok || !data.result || !data.result.customer || !data.result.verificationId) {
          throw new Error('Verification checkout failed');
        }
        const { firstname, lastname } = data.result.customer;

        this.form.firstName = firstname;
        this.form.lastName = lastname;
        this.verificationId = data.result.verificationId;
        this.isVerified = true;
      } catch (error) {
        console.error('Verification checkout error:', error);
        alert(this.texts.form_verify_error_message);
      } finally {
        this.isVerifying = false;
      }
    },
    submitForm() {
      // Show captcha modal instead of submitting directly
      this.showCaptchaModal = true;
      this.captchaError = null;
      this.$nextTick(() => {
        this.loadRecaptcha();
      });
    },
    loadRecaptcha() {
      // Check if reCAPTCHA script is already loaded
      if (window.grecaptcha && window.grecaptcha.render) {
        this.renderRecaptcha();
        return;
      }

      // Load reCAPTCHA script dynamically
      if (!document.getElementById('recaptcha-script')) {
        const script = document.createElement('script');
        script.id = 'recaptcha-script';
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        // Set up global callback
        window.onRecaptchaLoad = () => {
          this.renderRecaptcha();
        };
      } else if (window.grecaptcha) {
        this.renderRecaptcha();
      }
    },
    renderRecaptcha() {
      if (!this.$refs.recaptchaContainer) return;
      
      // Clear any existing widget
      this.$refs.recaptchaContainer.innerHTML = '';
      
      this.recaptchaWidgetId = window.grecaptcha.render(this.$refs.recaptchaContainer, {
        sitekey: this.recaptchaSiteKey,
        callback: this.onCaptchaSuccess,
        'expired-callback': this.onCaptchaExpired
      });
    },
    onCaptchaSuccess(response) {
      this.captchaResponse = response;
      this.captchaError = null;
      // Auto-submit after successful captcha
      this.finalSubmit();
    },
    onCaptchaExpired() {
      this.captchaResponse = null;
      this.captchaError = 'Captcha expired. Please try again.';
    },
    closeCaptchaModal() {
      this.showCaptchaModal = false;
      this.captchaResponse = null;
      if (window.grecaptcha && this.recaptchaWidgetId !== null) {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      }
    },
    /**
     * Generate a hash of the current form data for comparison
     * Used to detect if form content has changed between submissions
     */
    getFormDataHash() {
      const formData = {
        firstName: this.form.firstName || '',
        lastName: this.form.lastName || '',
        address: this.form.address || '',
        zipCode: this.form.zipCode || '',
        city: this.form.city || '',
        countryCode: this.form.countryCode || '',
        phone: this.form.phone || '',
        email: this.form.email || '',
        goldGrams: this.form.goldGrams || '',
        silverGrams: this.form.silverGrams || '',
        quantity: this.form.quantity || '',
        isBusiness: this.form.isBusiness || false,
        businessName: this.form.businessName || '',
        organizationNumber: this.form.organizationNumber || ''
      };
      // Create a simple hash from the JSON string
      const str = JSON.stringify(formData);
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString();
    },
    // Generate new GUID if the form data has changed since last submission
    getSubmissionGuid() {
      const currentHash = this.getFormDataHash();
      
      // If form data has changed since last submit button click.
      if (this.lastSubmittedFormHash !== currentHash) {
        this.submissionGuid = crypto.randomUUID();
        this.lastSubmittedFormHash = currentHash;
      }
      
      // If first time submit. (no guid yet)
      if (!this.submissionGuid) {
        this.submissionGuid = crypto.randomUUID();
        this.lastSubmittedFormHash = currentHash;
      }
      
      return this.submissionGuid;
    },
    finalSubmit() {
      // Close modal
      this.showCaptchaModal = false;
      this.isSubmitting = true;
      
      const submitData = {
        guid: this.getSubmissionGuid(),
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        address: this.form.address,
        zipCode: this.form.zipCode,
        city: this.form.city,
        phone: this.form.countryCode + this.form.phone,
        email: this.form.email,
        goldGrams: this.form.goldGrams || null,
        silverGrams: this.form.silverGrams || null,
        quantity: this.texts.form_quantity_enabled ? this.form.quantity : null,
        isBusiness: this.form.isBusiness || false,
        businessName: this.form.isBusiness ? this.form.businessName : null,
        organizationNumber: this.form.isBusiness ? this.form.organizationNumber : null,
        acceptedTermsAndConditions: this.acceptedTAC,
        acceptedTermsOfBusiness: this.acceptedTOB,
        storeCode: this.storeCode,
        verificationId: this.verificationId,
        captchaResponse: this.captchaResponse
      };
      
      // Submit to WordPress backend
      fetch(this.submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      })
        .then(response => {
          this.isSubmitting = false;
          if (response.ok) {
            // Response: 200 OK
            alert(this.texts.form_success_message);
            this.step = 'calculator';
            // Reset form
            this.form = {
              firstName: '',
              lastName: '',
              address: '',
              zipCode: '',
              city: '',
              countryCode: this.texts.form_phone_country_code,
              phone: '',
              email: '',
              goldGrams: '',
              silverGrams: '',
              quantity: '',
              isBusiness: false,
              businessName: '',
              organizationNumber: ''
            };
            this.isVerified = false;
            this.verificationId = null;
          } else {
            // Response: Not 200 OK
            // A verification can only be used once and expires after 30 min, so let the user verify again.
            this.isVerified = false;
            this.verificationId = null;
            alert(this.texts.form_error_message);
          }
        })
        .catch(error => {
          this.isSubmitting = false;
          console.error('Form submission error:', error);
          alert(this.texts.form_error_message);
        })
        .finally(() => {
          // Reset captcha for next submission
          this.captchaResponse = null;
        });
    }
  }
};
</script>

<style scoped>
.metal-calculator-shortcode {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background: transparent;
  scroll-margin-top: 100px;
}

.o-metal-calculator {
  color: #fff;
  background: transparent;
  padding: 48px 24px;
  max-width: 800px;
  margin: 0 auto;
}

.o-top-description {
  margin: 0 0 40px 0;
  text-align: center;
  font-size: 19px;
  color: #ccc;
}

.o-header h2 {
  font-size: 28px;
  font-weight: 500;
  margin: 0 0 16px 0;
  text-align: center;
}

.o-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 16px;
}

.control label {
  display: block;
  font-size: 14px;
  margin-bottom: 8px;
}

.value-box {
  background: #fff;
  border: 1px solid #bd9a5f;
  border-radius: 6px;
  padding: 0 12px;
  margin: 0 0 10px 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  box-sizing: border-box;
  line-height: 1;
}

.gram-input {
  background: transparent;
  border: none;
  color: #111;
  font-size: 16px;
  font-weight: 600;
  width: 80px;
  text-align: left;
  padding: 0;
  margin: 0;
  height: 100%;
  line-height: 44px;
}

.gram-input:focus {
  outline: none;
}

/* Hide number input spinners */
.gram-input::-webkit-outer-spin-button,
.gram-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
.gram-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.gram-suffix {
  color: #111;
  font-weight: 600;
}

input[type="range"] {
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: linear-gradient(to right, #bd9a5f 0%, #bd9a5f var(--range-progress, 50%), #555 var(--range-progress, 50%), #555 100%);
  border-radius: 4px;
  outline: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #bd9a5f;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  margin-top: -5px;
}

input[type="range"]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #bd9a5f;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

input[type="range"]::-ms-thumb {
  width: 18px;
  height: 18px;
  background: #bd9a5f;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

input[type="range"]::-webkit-slider-runnable-track {
  height: 8px;
  background: transparent;
  border-radius: 4px;
}

input[type="range"]::-moz-range-track {
  height: 8px;
  background: #555;
  border-radius: 4px;
}

input[type="range"]::-moz-range-progress {
  height: 8px;
  background: #bd9a5f;
  border-radius: 4px 0 0 4px;
}

select {
  width: 100%;
  height: 44px;
  padding: 0 36px 0 12px;
  margin: 0 0 10px 0;
  background: #fff;
  color: #111;
  border: 1px solid #bd9a5f;
  border-radius: 6px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23bd9a5f' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  box-sizing: border-box;
  line-height: 44px;
}

select:focus {
  outline: none;
  border-color: #d4af37;
}

select option {
  background: #fff;
  color: #111;
}

.o-result {
  margin: 20px 0;
}

.o-result .result-label {
  display: block;
  font-size: 14px;
  margin-bottom: 8px;
}

.result-box {
  background: #fff;
  border: 1px solid #bd9a5f;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 18px;
  font-weight: 700;
  color: #111;
}

.last-updated {
  display: block;
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

.o-description {
  margin: 24px 0;
  text-align: center;
}

.o-description p {
  color: #ccc;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}

.o-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
}

.btn {
  display: inline-block;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  border: 1px solid #bd9a5f;
  background: transparent;
  color: #fff;
  text-align: center;
}

.btn.primary {
  background: #bd9a5f;
  color: #000 !important;
  font-weight: bold !important;
  border-color: #bd9a5f;
  transition: background-color 0.3s ease;
}

.btn.primary:hover {
  background: #7f715c;
  border-color: #7f715c;
}

.btn.secondary {
  background: transparent;
}

/* Form Step Styles */
.o-form-step {
  background: transparent;
  padding: 10px 5px;
  max-width: 800px;
  margin: 0 auto;
}

.form-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;
}

.form-only .form-container {
  display: block;
  max-width: 400px;
  margin: 0 auto;
}

.contact-form {
  background: transparent;
  border: none;
  padding: 0;
}

.form-row {
  margin-bottom: 12px;
}

.form-row.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  position: relative;
}

.form-field label {
  color: #fff;
  font-size: 14px;
  margin-bottom: 6px;
}

.contact-form input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #bd9a5f;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff !important;
  color: #111 !important;
}

.contact-form input::placeholder {
  color: #666 !important;
}

.contact-form input:focus {
  outline: none;
  border-color: #d4af37;
}

.contact-form input:disabled {
  background: #e0e0e0 !important;
  color: #666 !important;
  cursor: not-allowed;
}

.phone-input-group {
  display: flex;
}

.phone-input-group input.phone-country-code {
  width: 68px;
  flex: 0 0 auto;
  padding: 12px 8px;
  border-right: none;
  border-radius: 6px 0 0 6px;
  text-align: center;
  font-weight: 600;
}

.phone-input-group input[type="tel"] {
  border-radius: 0 6px 6px 0;
}

.form-acceptance {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.acceptance-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #ccc;
  cursor: pointer;
  line-height: 1.3;
}

.acceptance-checkbox input[type="checkbox"] {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  accent-color: #bd9a5f;
  margin: 0;
}

.acceptance-checkbox a {
  color: #bd9a5f;
  text-decoration: underline;
  font-size: 13px !important;
}

.form-actions {
  margin-top: 20px;
  text-align: center;
}

.form-actions .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-actions .btn {
  width: 100%;
  max-width: 280px;
  padding: 14px 24px;
  font-size: 16px;
}

.form-actions .btn-verify {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #000;
  border-color: #ccc;
  color: #fff;
  font-weight: bold;
}

.btn-verify .telia-logo {
  height: 22px;
  width: auto;
}

.checkbox-field {
  margin-bottom: 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #fff;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #bd9a5f;
  cursor: pointer;
}

.privacy-notice {
  margin-top: 16px;
  font-size: 12px;
  color: #999;
  text-align: center;
  line-height: 1.5;
}

.privacy-notice a {
  color: #bd9a5f;
  text-decoration: underline;
  font-size: 13px !important;
}

.info-panel {
  padding: 0 5px;
}

.info-panel h3 {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 20px 0;
}

.info-panel .info-content {
  font-size: 18px;
  color: #fff;
  line-height: 1.6;
}

.info-panel .info-content strong {
  font-weight: 700;
  color: #ffffff;
}

.info-panel ul {
  list-style: disc;
  padding-left: 20px;
  margin: 0;
  color: #bd9a5f;
}

.info-panel li {
  font-size: 16px;
  color: #ccc;
  margin-bottom: 16px;
  line-height: 1.5;
}

.info-panel li strong {
  font-weight: 700;
  color: #fff;
}

.btn-back {
  margin-top: 24px;
  background: none;
  border: none;
  color: #bd9a5f;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  display: block;
}

.btn-back:hover {
  text-decoration: underline;
}

/* Captcha Modal Styles */
.captcha-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.captcha-modal {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  position: relative;
  max-width: 400px;
  width: 90%;
  text-align: center;
}

.captcha-modal h3 {
  margin: 0 0 24px 0;
  color: #111;
  font-size: 20px;
  font-weight: 600;
}

.captcha-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.captcha-close:hover {
  color: #111;
}

#recaptcha-container {
  display: flex;
  justify-content: center;
}

.captcha-error {
  color: #dc3545;
  font-size: 14px;
  margin-top: 16px;
}

@media (max-width: 768px) {
  .form-container {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .form-row.two-col {
    grid-template-columns: 1fr;
  }

  .info-panel {
    padding: 0;
  }
}
</style>

<!-- Google renders the Places dropdown outside the component, so it cannot be scoped -->
<style>
.pac-container {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  width: 100% !important;
  z-index: 10000;
}
</style>